import { takeEvery, all, call, put } from 'redux-saga/effects';
import * as types from './sampleActionTypes';
import * as actions from './sampleActions';
import * as endpoints from '../../services/endpoints';
import * as alert from '../alert/alertActions';
import { errorHandler } from '../../helpers/errorHandler';

const normalizeSampleFactorRow = (row, fallbackThreadType) => ({
  sampleFactorId: row?.sampleFactorId || row?.SampleFactorId || '',
  threadType: String(row?.threadType ?? row?.ThreadType ?? fallbackThreadType ?? '').trim(),
  firstColon: String(
    row?.firstColon ?? row?.FirstColon ?? row?.start ?? row?.Start ?? ''
  ).trim(),
  secondColon: String(
    row?.secondColon ?? row?.SecondColon ?? row?.end ?? row?.End ?? ''
  ).trim(),
  factor: String(row?.factor ?? row?.Factor ?? row?.multiplier ?? row?.Multiplier ?? '').trim(),
});

const flattenSampleFactorRows = (sampleFactorRowsByThreadType) => {
  if (!sampleFactorRowsByThreadType) return [];

  if (Array.isArray(sampleFactorRowsByThreadType)) {
    return sampleFactorRowsByThreadType
      .map(row => normalizeSampleFactorRow(row))
      .filter(row => row.threadType && row.firstColon && row.secondColon && row.factor);
  }

  const threadType1Rows = Array.isArray(sampleFactorRowsByThreadType[1])
    ? sampleFactorRowsByThreadType[1]
    : [];
  const threadType2Rows = Array.isArray(sampleFactorRowsByThreadType[2])
    ? sampleFactorRowsByThreadType[2]
    : [];

  return [
    ...threadType1Rows.map(row => normalizeSampleFactorRow(row, 1)),
    ...threadType2Rows.map(row => normalizeSampleFactorRow(row, 2)),
  ].filter(row => row.threadType && row.firstColon && row.secondColon && row.factor);
};

const toScaledWeightInt = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.trunc(parsed);
};

function* replaceSampleFactors({
  sessionUserId,
  sampleId,
  sampleFactorRowsByThreadType,
}) {
  if (!sampleId) return;

  // 1. Fetch all existing factors for this sample
  const existingResponse = yield call(endpoints.getSampleFactors, {
    SessionUserId: sessionUserId,
    SampleId: String(sampleId),
    PrdSampleGuid: String(sampleId),
  });
  const existingFactors = existingResponse?.data?.SampleFactorList || [];

  // 2. Delete every existing factor
  for (const existing of existingFactors) {
    const existingId = String(existing?.SampleFactorId || '').trim();
    if (existingId) {
      console.log('Deleting SampleFactor:', existingId);
      try {
        yield call(endpoints.deleteSampleFactor, {
          SessionUserId: sessionUserId,
          SampleFactorId: existingId,
        });
      } catch (deleteError) {
        console.error('SampleFactor delete error:', deleteError);
      }
    }
  }

  // 3. Create the new/remaining factor rows
  const factorRows = flattenSampleFactorRows(sampleFactorRowsByThreadType);
  for (const row of factorRows) {
    const createPayload = {
      SessionUserId: sessionUserId,
      PrdSampleId: String(sampleId),
      SampleId: String(sampleId),
      PrdSampleGuid: String(sampleId),
      ThreadType: row.threadType,
      FirstColon: row.firstColon,
      SecondColon: row.secondColon,
      Factor: row.factor,
    };
    console.log('Creating SampleFactor:', createPayload);
    yield call(endpoints.createSampleFactor, createPayload);
  }
}

function* syncSampleFactors({
  sessionUserId,
  sampleId,
  sampleFactorRowsByThreadType,
  allowUpdate,
}) {
  if (!sampleId) return;

  if (allowUpdate) {
    yield call(replaceSampleFactors, {
      sessionUserId,
      sampleId,
      sampleFactorRowsByThreadType,
    });
    return;
  }

  // Create-only path (new sample)
  const factorRows = flattenSampleFactorRows(sampleFactorRowsByThreadType);
  if (factorRows.length === 0) return;

  for (const row of factorRows) {
    const createPayload = {
      SessionUserId: sessionUserId,
      PrdSampleId: String(sampleId),
      SampleId: String(sampleId),
      PrdSampleGuid: String(sampleId),
      ThreadType: row.threadType,
      FirstColon: row.firstColon,
      SecondColon: row.secondColon,
      Factor: row.factor,
    };
    console.log('Creating SampleFactor:', createPayload);
    yield call(endpoints.createSampleFactor, createPayload);
  }
}

function* createSampleColors({
  sessionUserId,
  sampleId,
  tableData,
}) {
  if (!sampleId || !Array.isArray(tableData) || tableData.length === 0) return;

  const rowSortingByThreadType = {};
  for (let rowIndex = 0; rowIndex < tableData.length; rowIndex++) {
    const row = tableData[rowIndex];
    const yarnGuid = row?.yarnId || row?.PrdYarnGuid;
    const colorGuid = row?.colorId || row?.PrdColorGuid;
    const threadTypeValue = row?.threadType ?? row?.ThreadType;
    const values = Array.isArray(row?.values) ? row.values : [];
    const threadTypeKey = String(threadTypeValue || '');
    rowSortingByThreadType[threadTypeKey] = (rowSortingByThreadType[threadTypeKey] || 0) + 1;
    const sampleRowSorting = row?.sampleRowSorting ||
      row?.SampleRowSorting ||
      rowSortingByThreadType[threadTypeKey];

    const isValidThreadType = (
      threadTypeValue === 1 ||
      threadTypeValue === 2 ||
      threadTypeValue === '1' ||
      threadTypeValue === '2'
    );

    if (colorGuid && yarnGuid && isValidThreadType) {
      for (let colIndex = 0; colIndex < values.length; colIndex++) {
        const threadCount = parseInt(values[colIndex], 10) || 0;

        if (threadCount > 0) {
          const rawRowWeight = row?.weight ?? row?.Weight ?? row?.WaftWeight ?? row?.WeftWeight;
          const scaledWeight = toScaledWeightInt(rawRowWeight);
          const threadType = Number(threadTypeValue);
          const weftWeight = threadType === 1 ? scaledWeight : 0;
          const waftWeight = threadType === 2 ? scaledWeight : 0;
          const sampleColorPayload = {
            SessionUserId: sessionUserId,
            PrdSampleGuid: String(sampleId),
            PrdYarnGuid: yarnGuid,
            PrdColorGuid: colorGuid,
            Position: String(colIndex + 1),
            ThreadCount: String(threadCount),
            ThreadType: String(threadTypeValue),
            SampleRowSorting: String(sampleRowSorting),
            WeftWeight: weftWeight,
            WaftWeight: waftWeight
          };

          try {
            yield call(endpoints.addSampleColor, sampleColorPayload);
          } catch (sampleColorError) {
            console.error('SampleColor creation error:', sampleColorError);
          }
        }
      }
    }
  }
}

function* replaceSampleColors({
  sessionUserId,
  sampleId,
  tableData,
}) {
  if (!sampleId) return;

  const existingColorsResponse = yield call(endpoints.getSampleColors, {
    SessionUserId: sessionUserId,
    PrdSampleGuid: String(sampleId)
  });

  const existingColors = existingColorsResponse.data?.SampleColorList || [];
  for (const existingColor of existingColors) {
    if (existingColor.SampleColorId) {
      try {
        yield call(endpoints.deleteSampleColor, {
          SessionUserId: sessionUserId,
          SampleColorId: existingColor.SampleColorId
        });
      } catch (deleteError) {
        console.error('SampleColor delete error:', deleteError);
      }
    }
  }

  yield call(createSampleColors, {
    sessionUserId,
    sampleId,
    tableData
  });
}

function* getSampleSaga(action) {
  try {
    const { data } = yield call(endpoints.getSample, action.payload);
    yield put(actions.getSampleSuccess(data));
  } catch (e) {
    yield put(actions.getSampleError(e));
  }
}

function* getSampleListSaga(action) {
  try {
    const { data } = yield call(endpoints.getSample, action.payload);
    yield put(actions.getSampleListSuccess(data));
  } catch (e) {
    yield put(actions.getSampleListError(e));
  }
}

function* addSampleSaga(action) {
  try {
    const { data } = yield call(endpoints.addSample, action.payload);
    yield put(actions.addSampleSuccess(data));
    if (data.Result && data.Result.ErrorCode === 0) {
      yield put(alert.setAlertAction({
        title: 'Başarılı!',
        text: 'Analiz başarıyla oluşturuldu',
        variant: 'success',
      }));
      if (action.payload.navigate) {
        setTimeout(() => {
          action.payload.navigate('/samples');
        }, 1500);
      }
    } else {
      yield put(alert.setAlertAction({
        title: 'Hata!',
        text: data.Result?.ErrorDescription || 'Analiz oluşturulurken bir hata oluştu',
        variant: 'danger',
      }));
    }
  } catch (e) {
    yield put(actions.addSampleError(e));
    yield put(alert.setAlertAction({
      title: 'Hata!',
      text: 'Analiz oluşturulurken bir hata oluştu',
      variant: 'danger',
    }));
  }
}

function* updateSampleSaga(action) {
  try {
    const isCompositePayload = Boolean(
      action?.payload?.sampleData ||
      action?.payload?.tableData ||
      action?.payload?.sampleFactorRowsByThreadType
    );

    if (!isCompositePayload) {
      const { data } = yield call(endpoints.updateSample, action.payload);
      yield put(actions.updateSampleSuccess(data));
      if (data.Result && data.Result.ErrorCode === 0) {
        yield put(alert.setAlertAction({
          title: 'Başarılı!',
          text: 'Analiz başarıyla güncellendi',
          variant: 'success',
        }));
        if (action.payload.navigate) {
          setTimeout(() => {
            action.payload.navigate('/samples');
          }, 1000);
        }
      } else {
        yield put(alert.setAlertAction({
          title: 'Hata!',
          text: data.Result?.ErrorDescription || 'Analiz güncellenirken bir hata oluştu',
          variant: 'danger',
        }));
      }
      return;
    }

    const {
      sampleData,
      tableData = [],
      navigate,
      sessionUserId,
      sampleFactorRowsByThreadType
    } = action.payload;

    const effectiveSessionUserId = sessionUserId || sampleData?.SessionUserId;
    const sampleId = sampleData?.SampleId || sampleData?.PrdSampleGuid || sampleData?.SampleGuid;

    const { data } = yield call(endpoints.updateSample, sampleData);
    yield put(actions.updateSampleSuccess(data));

    if (data.Result && data.Result.ErrorCode === 0) {
      if (sampleId) {
        try {
          yield call(syncSampleFactors, {
            sessionUserId: effectiveSessionUserId,
            sampleId,
            sampleFactorRowsByThreadType,
            allowUpdate: true,
          });
        } catch (sampleFactorError) {
          console.error('Error handling SampleFactor operations:', sampleFactorError);
        }

        try {
          yield call(replaceSampleColors, {
            sessionUserId: effectiveSessionUserId,
            sampleId,
            tableData
          });
        } catch (sampleColorError) {
          console.error('Error handling SampleColor operations:', sampleColorError);
        }
      }

      yield put(alert.setAlertAction({
        title: 'Başarılı!',
        text: 'Analiz başarıyla güncellendi',
        variant: 'success',
      }));

      if (navigate) {
        setTimeout(() => {
          navigate('/samples');
        }, 1000);
      }
    } else {
      yield put(alert.setAlertAction({
        title: 'Hata!',
        text: data.Result?.ErrorDescription || 'Analiz güncellenirken bir hata oluştu',
        variant: 'danger',
      }));
    }
  } catch (e) {
    yield put(actions.updateSampleError(e));
    yield put(alert.setAlertAction({
      title: 'Hata!',
      text: 'Analiz güncellenirken bir hata oluştu',
      variant: 'danger',
    }));
  }
}

function* deleteSampleSaga(action) {
  try {
    const { data } = yield call(endpoints.deleteSample, action.payload);
    yield put(actions.deleteSampleSuccess(data));
    if (data.Result && data.Result.ErrorCode === 0) {
      yield put(alert.setAlertAction({
        title: 'Başarılı!',
        text: 'Analiz başarıyla silindi',
        variant: 'success',
      }));
      yield put(actions.getSampleRequest({}));
    } else {
      yield put(alert.setAlertAction({
        title: 'Hata!',
        text: data.Result?.ErrorDescription || 'Analiz silinirken bir hata oluştu',
        variant: 'danger',
      }));
    }
  } catch (e) {
    yield put(actions.deleteSampleError(e));
    yield put(alert.setAlertAction({
      title: 'Hata!',
      text: 'Analiz silinirken bir hata oluştu',
      variant: 'danger',
    }));
  }
}

function* getSampleDropdownSaga(action) {
  try {
    const { data } = yield call(endpoints.getSampleDropdown, action.payload);
    yield put(actions.getSampleDropdownSuccess(data));
  } catch (e) {
    yield put(actions.getSampleDropdownError(e));
  }
}

function* addSampleColorSaga(action) {
  try {
    const { data } = yield call(endpoints.addSampleColor, action.payload);
    yield put(actions.addSampleColorSuccess(data));
    if (data.Result && data.Result.ErrorCode !== 0) {
      yield put(alert.setAlertAction({
        title: 'Hata!',
        text: data.Result?.ErrorDescription || 'Analiz rengi eklenirken bir hata oluştu',
        variant: 'danger',
      }));
    }
  } catch (e) {
    yield put(actions.addSampleColorError(e));
  }
}

function* getSampleColorsSaga(action) {
  try {
    const { data } = yield call(endpoints.getSampleColors, action.payload);
    yield put(actions.getSampleColorsSuccess(data));
  } catch (e) {
    yield put(actions.getSampleColorsError(e));
  }
}

function* updateSampleColorSaga(action) {
  try {
    const { data } = yield call(endpoints.updateSampleColor, action.payload);
    yield put(actions.updateSampleColorSuccess(data));
    if (data.Result && data.Result.ErrorCode !== 0) {
      yield put(alert.setAlertAction({
        title: 'Hata!',
        text: data.Result?.ErrorDescription || 'Analiz rengi güncellenirken bir hata oluştu',
        variant: 'danger',
      }));
    }
  } catch (e) {
    yield put(actions.updateSampleColorError(e));
  }
}

function* deleteSampleColorSaga(action) {
  try {
    const { data } = yield call(endpoints.deleteSampleColor, action.payload);
    yield put(actions.deleteSampleColorSuccess(data));
    if (data.Result && data.Result.ErrorCode === 0) {
      yield put(alert.setAlertAction({
        title: 'Başarılı!',
        text: 'Analiz rengi başarıyla silindi',
        variant: 'success',
      }));
    } else {
      yield put(alert.setAlertAction({
        title: 'Hata!',
        text: data.Result?.ErrorDescription || 'Analiz rengi silinirken bir hata oluştu',
        variant: 'danger',
      }));
    }
  } catch (e) {
    yield put(actions.deleteSampleColorError(e));
  }
}

function* watchGetSample() {
  yield takeEvery(types.GET_SAMPLE_REQUEST, getSampleSaga);
}

function* watchGetSampleList() {
  yield takeEvery(types.GET_SAMPLE_LIST_REQUEST, getSampleListSaga);
}

function* watchAddSample() {
  yield takeEvery(types.ADD_SAMPLE_REQUEST, addSampleSaga);
}

function* watchUpdateSample() {
  yield takeEvery(types.UPDATE_SAMPLE_REQUEST, updateSampleSaga);
}

function* watchDeleteSample() {
  yield takeEvery(types.DELETE_SAMPLE_REQUEST, deleteSampleSaga);
}

function* watchGetSampleDropdown() {
  yield takeEvery(types.GET_SAMPLE_DROPDOWN_REQUEST, getSampleDropdownSaga);
}

function* watchAddSampleColor() {
  yield takeEvery(types.ADD_SAMPLE_COLOR_REQUEST, addSampleColorSaga);
}

function* watchGetSampleColors() {
  yield takeEvery(types.GET_SAMPLE_COLORS_REQUEST, getSampleColorsSaga);
}

function* watchUpdateSampleColor() {
  yield takeEvery(types.UPDATE_SAMPLE_COLOR_REQUEST, updateSampleColorSaga);
}

function* watchDeleteSampleColor() {
  yield takeEvery(types.DELETE_SAMPLE_COLOR_REQUEST, deleteSampleColorSaga);
}

function* createSampleWithColorsSaga(action) {
  try {
    const {
      sampleData,
      tableData,
      navigate,
      sessionUserId,
      sampleFactorRowsByThreadType
    } = action.payload;

    // Step 1: Create the sample
    const { data } = yield call(endpoints.addSample, sampleData);

    if (data.Result && data.Result.ErrorCode === 0) {
      // Step 2: Use SampleId returned by AddSample response
      const sampleId = data?.PrdSampleId ||
        data?.SampleId ||
        data?.PrdSampleGuid ||
        data?.Sample?.PrdSampleId ||
        data?.Sample?.SampleId ||
        data?.Sample?.PrdSampleGuid ||
        data?.Sample?.SampleGuid;

      // Step 3: Create SampleColor entries for each row
      if (sampleId) {
        try {
          yield call(syncSampleFactors, {
            sessionUserId,
            sampleId,
            sampleFactorRowsByThreadType,
            allowUpdate: false,
          });
        } catch (sampleFactorError) {
          console.error('Error handling SampleFactor operations:', sampleFactorError);
        }

        try {
          yield call(createSampleColors, {
            sessionUserId,
            sampleId,
            tableData
          });
        } catch (sampleColorError) {
          console.error('Error handling SampleColor operations:', sampleColorError);
        }
      } else {
        console.error(
          'SampleId could not be resolved after AddSample. Skipping SampleColor/SampleFactor.'
        );
      }

      yield put(actions.createSampleWithColorsSuccess(data));
      yield put(alert.setAlertAction({
        title: 'Başarılı!',
        text: 'Analiz başarıyla oluşturuldu',
        variant: 'success',
      }));

      if (navigate) {
        setTimeout(() => {
          navigate('/samples');
        }, 1500);
      }
    } else {
      yield put(actions.createSampleWithColorsError(data.Result?.ErrorDescription ||
        'Analiz oluşturulurken bir hata oluştu'));
      yield put(alert.setAlertAction({
        title: 'Hata!',
        text: data.Result?.ErrorDescription || 'Analiz oluşturulurken bir hata oluştu',
        variant: 'danger',
      }));
    }
  } catch (e) {
    yield put(actions.createSampleWithColorsError(e));
    yield put(alert.setAlertAction({
      title: 'Hata!',
      text: 'Analiz oluşturulurken bir hata oluştu',
      variant: 'danger',
    }));
  }
}

function* watchCreateSampleWithColors() {
  yield takeEvery(types.CREATE_SAMPLE_WITH_COLORS_REQUEST, createSampleWithColorsSaga);
}

function* createWeaveFromSampleSaga(action) {
  try {
    const sessionUserId = action?.payload?.SessionUserId || localStorage.getItem('sessionUserId');
    const sampleGuid = action?.payload?.PrdSampleGuid ||
      action?.payload?.SampleId ||
      action?.payload?.SampleGuid;

    const createPayload = {
      ...(action.payload || {}),
      SessionUserId: sessionUserId,
      PrdSampleGuid: sampleGuid ? String(sampleGuid) : action?.payload?.PrdSampleGuid,
    };

    const { data } = yield call(endpoints.createWeaveFromSample, createPayload);

    if (data.Result && data.Result.ErrorCode === 0) {
      yield put(actions.createWeaveFromSampleSuccess(data));
      yield put(alert.setAlertAction({
        title: 'Başarılı!',
        text: 'Ürün başarıyla oluşturuldu!',
        variant: 'success',
      }));
    } else {
      yield put(actions.createWeaveFromSampleError(data.Result?.ErrorDescription ||
        'Ürün oluşturulurken bir hata oluştu'));
      yield put(alert.setAlertAction({
        title: 'Hata!',
        text: data.Result?.ErrorDescription || 'Ürün oluşturulurken bir hata oluştu',
        variant: 'danger',
      }));
    }
  } catch (e) {
    console.error('createWeaveFromSampleSaga error:', e);
    yield put(actions.createWeaveFromSampleError(e));
    yield call(errorHandler, e);
  }
}

function* watchCreateWeaveFromSample() {
  yield takeEvery(types.CREATE_WEAVE_FROM_SAMPLE_REQUEST, createWeaveFromSampleSaga);
}

export function* sampleSaga() {
  yield all([
    watchGetSample(),
    watchGetSampleList(),
    watchAddSample(),
    watchUpdateSample(),
    watchDeleteSample(),
    watchGetSampleDropdown(),
    watchAddSampleColor(),
    watchGetSampleColors(),
    watchUpdateSampleColor(),
    watchDeleteSampleColor(),
    watchCreateSampleWithColors(),
    watchCreateWeaveFromSample(),
  ]);
}


